import React, {
  useState,
  useEffect,
  useRef,
} from "react";
import { useDrag, useDrop } from "react-dnd";
import ExcelJS from "exceljs";
import DatePicker from "react-datepicker";
import { MapContainer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "react-datepicker/dist/react-datepicker.css";

import * as echarts from "echarts";
import {
  PlusCircle,
  Edit2,
  X,
  BarChart3,
  MapPin,
  PieChart as PieChartIcon,
  Table,
  Move,
  Minus,
  LineChart as LineChartIcon,
  AreaChart as AreaChartIcon,
  Download,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Calendar,
  Trash,
  Plus,
  ChevronsRight,
  ChevronsDown,
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Radar as RadarRechart,
  RadialBarChart,
  RadialBar,
} from "recharts";
import { useLocation, useNavigate } from "react-router-dom";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";


const ItemTypes = {
  CARD: "card",
  CHART: "chart",
  TABLE: "table",
  MAP: "map",
};

const chartColors = [
  "#1a7a73", // blue-500
  "#10B981", // emerald-500
  "#8B5CF6", // violet-500
  "#EF4444", // red-500
  "#F59E0B", // amber-500
  "#EC4899", // pink-500
  "#14B8A6", // teal-500
  "#84CC16", // lime-500
];

const iconMap = {
  "Atm's": new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/10410/10410410.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  "Bank's": new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/10410/10410388.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  Incidents: new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/564/564619.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  "Cash vans": new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854894.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  default: new L.Icon.Default(),
};
const TrendChart = ({
  data,
  xAxisKey,
  yAxisKeys,
  colors,
  height = 400,
  yAxisLabels = [],
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);
    // Create mapping from key to custom label
    const labelMap = {};
    yAxisKeys.forEach((key, index) => {
      labelMap[key] = yAxisLabels[index] || key;
    });

    const option = {
      tooltip: {
        trigger: "axis",
        formatter: function (params) {
          let result = params[0].name + "<br/>";
          params.forEach((param) => {
            const displayName = labelMap[param.seriesName] || param.seriesName;
            result += `${param.marker} ${param.seriesName}: ${param.value}<br/>`;
          });
          return result;
        },
      },
      legend: {
        data: yAxisKeys.map((key) => labelMap[key] || key),
        bottom: 0,
      },
      grid: {
        left: "3%",
        right: "15%", // Increased right margin to accommodate end labels
        bottom: "15%",
        top: "5%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: data.map((item) => item[xAxisKey]),
        axisLine: {
          lineStyle: {
            color: "#666",
          },
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            opacity: 0.3,
          },
        },
      },
      
      series: yAxisKeys.map((key, index) => ({
        name: labelMap[key] || key,
        type: "line",
        data: data.map((item) => item[key]),
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: {
          width: 3,
          color: colors[index % colors.length],
        },
        itemStyle: {
          color: colors[index % colors.length],
        },
        // Add end label configuration
        endLabel: {
          show: true,
          formatter: function (params) {
            const displayName =
              labelMap[params.seriesName] || params.seriesName;
            return `${displayName}: ${params.value}`;
          },
          distance: 8, // Distance from the line end point
          color: colors[index % colors.length],
          fontSize: 12,
          fontWeight: "bold",
        },
        emphasis: {
          focus: "series",
          lineStyle: {
            width: 5,
            shadowBlur: 10,
            shadowColor: colors[index % colors.length],
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: "#fff",
            shadowBlur: 5,
            shadowColor: colors[index % colors.length],
          },
        },
      })),
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [data, xAxisKey, yAxisKeys, colors, yAxisLabels]);

  return (
    <div ref={chartRef} style={{ width: "100%", height: `${height}px` }} />
  );
};
export const DraggableCard = ({
  item,
  index,
  moveItem,
  handleEdit,
  handleDelete,
  renderIcon,
  isRemoved = false,
  handleRestore,
  handleDuplicate,
  isEditingLayout,
  tablePage = 1,
  tableRowsPerPage = 5,
  onTablePageChange,
  onTableRowsPerPageChange,
  uploadedIcons,
  iconSizes,
  isViewMode,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandLevel, setExpandLevel] = useState(item.expandLevel || 1); // 1, 2, or 3 for columns
  const [heightExpandLevel, setHeightExpandLevel] = useState(
    item.heightExpandLevel || 1
  ); // 1, 2, or 3 for rows
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(item.data || []);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const ref = useRef(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [{ isDragging }, drag] = useDrag({
    type:
      item.type === "card"
        ? ItemTypes.CARD
        : item.type === "chart"
        ? ItemTypes.CHART
        : ItemTypes.TABLE,
    item: { id: item.id, index, isRemoved },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const applyDateFilter = () => {
    setAppliedDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  useEffect(() => {
    // This will force a re-render when item.data changes
  }, [item.data]);

  const resetDateFilter = () => {
    setDateRange({ startDate: null, endDate: null });
    setAppliedDateRange({ startDate: null, endDate: null });
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const toggleMinimize = () => {
    if (!isRemoved && handleEdit) {
      handleEdit(item.id, "minimized", !item.minimized);
    }
  };

  const toggleExpand = () => {
    const newExpandLevel = expandLevel === 1 ? 2 : expandLevel === 2 ? 3 : 1;
    setIsExpanded(!isExpanded);
    setExpandLevel(newExpandLevel);
    handleEdit(item.id, "expandLevel", newExpandLevel);
  };

  const toggleHeightExpand = () => {
    const newHeightExpandLevel =
      heightExpandLevel === 1 ? 2 : heightExpandLevel === 2 ? 3 : 1;
    setHeightExpandLevel(newHeightExpandLevel);
    handleEdit(item.id, "heightExpandLevel", newHeightExpandLevel);
  };

  useEffect(() => {
    if (item.type === "table" && item.data) {
      if (searchTerm) {
        const filtered = item.data.filter((row) =>
          item.columns.some((column) => {
            const value = String(row[column.key]).toLowerCase();
            return value.includes(searchTerm.toLowerCase());
          })
        );
        setFilteredData(filtered);
      } else {
        setFilteredData(item.data);
      }
    }
  }, [searchTerm, item.data, item.type === "table" ? item.columns : null]);

  
  const renderTable = (item) => {
    // Safely destructure with defaults
    const {
      data = [],
      columns = [],
      subColumns = [],
      showDownload = false,
      showSearch = false,
      showPagination = false,
      enableSorting = false,
      tableType = "basic",
      title = "",
      showDateFilter = false,
    } = item;

    // Add this helper function at the top of your component or in a utils file
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return "";

      try {
        // Check if it's already in yyyy-mm-dd format
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString; // Return original if invalid

        // Format as dd-mm-yyyy
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
      } catch (error) {
        return dateString; // Return original if parsing fails
      }
    };

    const dateColumn =
      columns.find((col) => col.key === "created_on")?.key ||
      columns.find((col) => col.key.toLowerCase().includes("created_on"))?.key;
    const headerStyle = {
      backgroundColor: item.headerColor || "#f9fafb",
      color: item.headerTextColor || "#374151",
    };

    const subHeaderStyle = {
      backgroundColor: item.subHeaderColor || "#f1f5f9",
      color: item.subHeaderTextColor || "#334155",
    };

    // Sort columns by their order property before rendering
    const visibleColumns = columns
      .filter((c) => c.selected)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col) => ({
        ...col,
        // Use the custom label if available, otherwise generate from key
        label:
          col.label ||
          col.key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase()),
      }));

    const visibleSubColumns =
      tableType === "expandable"
        ? subColumns
            .filter((c) => c.selected)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((col) => ({
              ...col,
              label:
                col.label ||
                col.key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase()),
            }))
        : [];

    // Apply date filtering if date column exists and dates are selected
    let filteredData = [...data];
    if (
      showDateFilter &&
      dateColumn &&
      (appliedDateRange.startDate || appliedDateRange.endDate)
    ) {
      filteredData = data.filter((row) => {
        const rowDate = new Date(row[dateColumn]);
        const startValid =
          !appliedDateRange.startDate ||
          rowDate >= new Date(appliedDateRange.startDate);
        // Modified to include the entire end date (up to 23:59:59.999)
        const endValid =
          !appliedDateRange.endDate ||
          rowDate <=
            new Date(
              new Date(appliedDateRange.endDate).setHours(23, 59, 59, 999)
            );
        return startValid && endValid;
      });
    }

    // Apply search filtering if there's a search term
    if (searchTerm && showSearch) {
      filteredData = filteredData.filter((row) =>
        columns.some((column) => {
          const value = String(row[column.key] || "").toLowerCase();
          return value.includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply sorting if enabled
    let sortedData = [...filteredData];
    if (enableSorting && sortConfig.key) {
      sortedData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    // Calculate pagination
    const totalRows = sortedData.length;
    const indexOfLastRow = tablePage * tableRowsPerPage;
    const indexOfFirstRow = indexOfLastRow - tableRowsPerPage;
    const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(totalRows / tableRowsPerPage);

    const getBadgeColor = (value) => {
      if (value <= 5) return "#10B981";
      if (value <= 10) return "#F59E0B";
      return "#EF4444";
    };

    const downloadExcel = async () => {
      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Data");

      // Prepare header row
      const headerRow = visibleColumns.map((col) => col.label);

      // Add sub-column headers for expandable tables
      if (item.tableType === "expandable" && visibleSubColumns.length > 0) {
        headerRow.push(...visibleSubColumns.map((col) => col.label));
      }

      // Add header row to worksheet
      worksheet.addRow(headerRow);

      // Style the header row (optional)
      const headerRowIndex = 1;
      worksheet.getRow(headerRowIndex).font = { bold: true };
      worksheet.getRow(headerRowIndex).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      // Add data rows
      sortedData.forEach((row) => {
        const dataRow = [];

        // Main columns data
        visibleColumns.forEach((col) => {
          dataRow.push(row[col.key] || "");
        });

        // Sub columns data for expandable tables
        if (item.tableType === "expandable") {
          visibleSubColumns.forEach((col) => {
            dataRow.push(row[col.key] || "");
          });
        }

        worksheet.addRow(dataRow);
      });

      // Auto-fit column widths
      worksheet.columns.forEach((column) => {
        column.width = 15; // Set a default width, or calculate based on content
      });

      // Generate Excel file buffer
      const buffer = await workbook.xlsx.writeBuffer();

      // Create download link
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    return (
      <div style={{ overflowX: "auto" }}>
        {showDateFilter && (
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "16px",
              alignItems: "center",
              padding: "12px",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              flexWrap: "wrap",
              position: "relative", // Add this
              zIndex: 25, // Add this - higher than table header's zIndex: 21
            }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <label style={{ fontSize: "14px", color: "#4b5563" }}>
                From:
              </label>
              <div style={{ position: "relative" }}>
                <Calendar
                  size={16}
                  color="#94a3b8"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                />
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, startDate: date }))
                  }
                  selectsStart
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  placeholderText="Start date"
                  className="custom-date-picker"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <label style={{ fontSize: "14px", color: "#4b5563" }}>To:</label>
              <div style={{ position: "relative" }}>
                <Calendar
                  size={16}
                  color="#94a3b8"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                />
                <DatePicker
                  selected={dateRange.endDate}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, endDate: date }))
                  }
                  selectsEnd
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  minDate={dateRange.startDate}
                  placeholderText="End date"
                  className="custom-date-picker"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>

            <button
              onClick={applyDateFilter}
              style={{
                padding: "8px 16px",
                backgroundColor: "#ffffff",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Filter size={16} />
              Apply
            </button>

            <button
              onClick={resetDateFilter}
              style={{
                padding: "8px 16px",
                backgroundColor: "#f3f4f6",
                color: "#4b5563",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <RotateCcw size={16} />
              Reset
            </button>
          </div>
        )}
        {(showSearch || showDownload) && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "16px",
              gap: "100px",
            }}
          >
            {showSearch && (
              <div style={{ position: "relative", flex: 1 }}>
                <Search
                  size={16}
                  color="#6b7280"
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search table..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "8px 12px 8px 36px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    outline: "none",
                    fontSize: "14px",
                    width:
                      expandLevel === 3
                        ? "60%"
                        : expandLevel === 2
                        ? "40%"
                        : "90%",
                  }}
                />
              </div>
            )}
            {showDownload && (
              <button
                onClick={downloadExcel}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#10B981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                <Download size={16} />
                Download Excel
              </button>
            )}
          </div>
        )}

        <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              position: "relative",
            }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 20,
                ...headerStyle,
              }}
            >
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                {tableType === "expandable" && (
                  <th
                    style={{
                      width: "40px",
                      padding: "12px 8px",
                      background: item.headerColor,
                      position: "sticky",
                      top: 0,
                      zIndex: 21,
                    }}
                  ></th>
                )}
                {visibleColumns.map((column) => (
                  <th
                    key={column.key}
                    style={{
                      padding: "12px 16px",
                      textAlign: "center",
                      fontWeight: "600",
                      color: item.headerTextColor || "#374151",
                      background: item.headerColor,
                      fontSize: "14px",
                      cursor: enableSorting ? "pointer" : "default",
                      position: "sticky",
                      top: 0,
                      zIndex: 21,
                    }}
                    onClick={() => enableSorting && handleSort(column.key)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        justifyContent: "center",
                      }}
                    >
                      {column.label}
                      {enableSorting &&
                        sortConfig.key === column.key &&
                        (sortConfig.direction === "ascending" ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        ))}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <tr
                    style={{
                      borderBottom: "1px solid #f3f4f6",
                      backgroundColor: expandedRows[rowIndex]
                        ? "#f8fafc"
                        : "transparent",
                    }}
                  >
                    {tableType === "expandable" && (
                      <td style={{ padding: "12px 8px" }}>
                        <button
                          onClick={() => toggleRow(rowIndex)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                          }}
                        >
                          {expandedRows[rowIndex] ? (
                            <Minus size={16} color="#64748b" />
                          ) : (
                            <Plus size={16} color="#64748b" />
                          )}
                        </button>
                      </td>
                    )}
                    {visibleColumns.map((column) => (
                      <td
                        key={column.key}
                        style={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#374151",
                        }}
                      >
                        {column.type === "badge" ? (
                          <span
                            style={{
                              backgroundColor: getBadgeColor(row[column.key]),
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            {row[column.key]}
                          </span>
                        ) : column.type === "date" ? (
                          // Format date for display while keeping original value for filtering
                          formatDateForDisplay(row[column.key])
                        ) : (
                          row[column.key]
                        )}
                      </td>
                    ))}
                  </tr>
                  {tableType === "expandable" && expandedRows[rowIndex] && (
                    <tr>
                      <td
                        colSpan={visibleColumns.length + 1}
                        style={{ padding: 0, position: "relative" }}
                      >
                        <div
                          style={{
                            padding: "16px 16px 16px 60px",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: "white",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                              position: "relative", // Add this
                              zIndex: 5,
                            }}
                          >
                            <div
                              style={{
                                padding: "16px",
                                borderBottom: "1px solid #e5e7eb",
                              }}
                            >
                              <h4
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  fontWeight: "600",
                                }}
                              >
                                {title} Detailed view
                              </h4>
                            </div>
                            <div
                              style={{
                                overflowX: "auto",
                                maxHeight: "300px", // Add max height
                                overflowY: "auto", // Add vertical scroll
                                position: "relative",
                              }}
                            >
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  position: "relative",
                                }}
                              >
                                <thead
                                  style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 25, // Higher than main header
                                  }}
                                >
                                  <tr
                                    style={{
                                      ...subHeaderStyle,
                                      borderBottom: "1px solid #e5e7eb",
                                    }}
                                  >
                                    {visibleSubColumns.map((col) => (
                                      <th
                                        key={col.key}
                                        style={{
                                          padding: "12px 16px",
                                          textAlign: "center",
                                          fontWeight: "600",
                                          background: item.subHeaderColor,
                                          color: item.subHeaderTextColor,
                                          position: "sticky",
                                          top: 0,
                                          zIndex: 26,
                                        }}
                                      >
                                        {col.label}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    {visibleSubColumns.map((col) => (
                                      <td
                                        key={col.key}
                                        style={{
                                          padding: "12px 16px",
                                          borderBottom: "1px solid #f3f4f6",
                                        }}
                                      >
                                        {row[col.key] || "N/A"}
                                      </td>
                                    ))}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {currentRows.length === 0 && (
          <div
            style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}
          >
            No matching records found
          </div>
        )}

        {showPagination && sortedData.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "16px",
              padding: "8px 0",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "14px", color: "#6b7280" }}>
                Rows per page:
              </span>
              <select
                value={tableRowsPerPage}
                onChange={(e) =>
                  onTableRowsPerPageChange(Number(e.target.value))
                }
                style={{
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  fontSize: "14px",
                  color: "#374151",
                }}
              >
                {[5, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "14px", color: "#6b7280" }}>
                {indexOfFirstRow + 1}-{Math.min(indexOfLastRow, totalRows)} of{" "}
                {totalRows}
              </span>
              <button
                onClick={() => onTablePageChange(Math.max(tablePage - 1, 1))}
                disabled={tablePage === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor: tablePage === 1 ? "#f3f4f6" : "#e5e7eb",
                  color: tablePage === 1 ? "#9ca3af" : "#374151",
                  border: "none",
                  borderRadius: "4px",
                  cursor: tablePage === 1 ? "not-allowed" : "pointer",
                }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() =>
                  onTablePageChange(Math.min(tablePage + 1, totalPages))
                }
                disabled={tablePage === totalPages}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    tablePage === totalPages ? "#f3f4f6" : "#e5e7eb",
                  color: tablePage === totalPages ? "#9ca3af" : "#374151",
                  border: "none",
                  borderRadius: "4px",
                  cursor: tablePage === totalPages ? "not-allowed" : "pointer",
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderChart = (item) => {
    const { chartType, data, title, xAxisKey, yAxisKeys, colors, yAxisLabels } =
      item;

    switch (chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <LineChart data={data} >
              {item.gridLines !== false && (
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              )}
              {/* <XAxis dataKey={xAxisKey} stroke="#666" /> */}
              {/* <XAxis
                dataKey={xAxisKey}
                stroke="#666"
                angle={item.xAxisOrientation === 'angled' ? -30 : item.xAxisOrientation === 'vertical' ? -90 : 0}
                textAnchor={item.xAxisOrientation === 'vertical' ? 'end' : 'middle'}
                height={item.xAxisOrientation !== 'horizontal' ? 60 : undefined}
              /> */}
              <XAxis
                dataKey={xAxisKey}
                stroke="#666"
                angle={item.xAxisOrientation === 'angled' ? -30 : item.xAxisOrientation === 'vertical' ? -90 : 0}
                textAnchor={item.xAxisOrientation === 'vertical' ? 'end' : 'middle'}
                height={
                  item.xAxisOrientation === 'angled' ? 60 :
                    item.xAxisOrientation === 'vertical' ? 100 :
                      undefined
                }
                tickMargin={item.xAxisOrientation === 'vertical' ? 25 :
                  item.xAxisOrientation === 'angled' ? 15 :
                    15}
                interval={
                  // Show fewer labels if vertical/angled to prevent overlap
                  item.xAxisOrientation !== 'horizontal' ? 'preserveStartEnd' : 0
                }
                tick={{
                  fontSize: item.xAxisOrientation === 'vertical' ? 10 : 12, // Smaller font for vertical
                }}
              />
              <YAxis stroke="#666" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value, name, props) => [
                  value,
                  item.yAxisLabels?.[props.dataKey] || name,
                ]}
              />
              {item.legendPosition !== "hidden" && (
                <Legend
                  layout={
                    item.legendPosition === "left" ||
                    item.legendPosition === "right"
                      ? "vertical"
                      : "horizontal"
                  }
                  verticalAlign={
                    item.legendPosition === "bottom"
                      ? "bottom"
                      : item.legendPosition === "top"
                      ? "top"
                      : "middle"
                  }
                  align={
                    item.legendPosition === "left"
                      ? "left"
                      : item.legendPosition === "right"
                      ? "right"
                      : "center"
                  }
                  // formatter={(value, entry, index) => {
                  //   return item.yAxisLabels?.[index] || value;
                  // }}
                />
              )}
              {yAxisKeys.map((key, index) => (
                <Line
                  key={key}
                  type={item.curveType || "monotone"}
                  dataKey={key}
                  stroke={colors[index % colors.length]}
                  strokeWidth={3}
                  // name={item.dataSource ? `${item.dataSource}` : key}
                  name={yAxisLabels?.[index] || key}
                  dot={{
                    fill: colors[index % colors.length],
                    strokeWidth: 2,
                    r: 4,
                  }}
                  isAnimationActive={item.animation !== "none"}
                  animationDuration={item.animation === "bounce" ? 2000 : 1000}
                  animationEasing={
                    item.animation === "bounce" ? "ease-out" : "ease-in-out"
                  }
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      case "radialProgress":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="90%"
              barSize={20}
              data={data}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis
                type="number"
                domain={[0, 100]}
                angleAxisId={0}
                tick={false}
              />
              <RadialBar
                background
                clockWise
                dataKey={yAxisKeys[0]}
                cornerRadius={10}
                fill={colors[0]}
              />

              {/* Name text - positioned above the value */}
              <text
                x="50%"
                y="45%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  fill: "#374151",
                }}
              >
                {data[0]?.[xAxisKey] || item.title || "Progress"}
              </text>

              {/* Value text - positioned below the name */}
              <text
                x="50%"
                y="55%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  fill: colors[0],
                }}
              >
                {`${data[0]?.[yAxisKeys[0]] || 0}%`}
              </text>

              {/* {item.legendPosition !== "hidden" && (
                  <Legend
                    layout={
                      item.legendPosition === "left" ||
                      item.legendPosition === "right"
                        ? "vertical"
                        : "horizontal"
                    }
                    verticalAlign={
                      item.legendPosition === "bottom"
                        ? "bottom"
                        : item.legendPosition === "top"
                        ? "top"
                        : "middle"
                    }
                    align={
                      item.legendPosition === "left"
                        ? "left"
                        : item.legendPosition === "right"
                        ? "right"
                        : "center"
                    }
                  />
                )} */}
            </RadialBarChart>
          </ResponsiveContainer>
        );

      case "progressBar":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              maxHeight:
                heightExpandLevel === 3
                  ? "800px"
                  : heightExpandLevel === 2
                  ? "500px"
                  : "300px",
              overflowY: "auto",
              overflowX: "hidden",
              padding: "16px",
            }}
          >
            {/* Y-Axis Labels on Top */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "20px",
                fontSize: "16px",
                fontWeight: "600",
                color: "#374151",
                paddingBottom: "12px",
                borderBottom: "1px solid #e5e7eb",
                position: "sticky",
                top: "0",
                backgroundColor: "white",
                zIndex: 10,
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: colors[0] || "#10b981",
                    borderRadius: "3px",
                  }}
                ></div>
                <span>{yAxisLabels?.[0] || yAxisKeys[0]}</span>
                {yAxisKeys.length > 1 && (
                  <>
                    <span
                      style={{
                        margin: "0 12px",
                        color: "#6b7280",
                        fontSize: "14px",
                      }}
                    >
                      vs
                    </span>
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: colors[1] || "#ef4444",
                        borderRadius: "3px",
                      }}
                    ></div>
                    <span>{yAxisLabels?.[1] || yAxisKeys[1]}</span>
                  </>
                )}
              </span>
            </div>

            {/* Scrollable Progress Bars Container */}
            <div
              style={{
                maxHeight: "calc(100% - 80px)",
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: "8px",
              }}
            >
              {data.map((entry, index) => {
                const firstValue = entry[yAxisKeys[0]] || 0;

                // Check if we have a second Y-axis key
                let secondValue = 0;
                let total = 100; // Default total for single value case
                let isSingleValue = true;

                if (yAxisKeys.length > 1 && yAxisKeys[1]) {
                  secondValue = entry[yAxisKeys[1]] || 0;
                  total = firstValue + secondValue;
                  isSingleValue = false;
                } else {
                  // Single value case - treat as percentage out of 100
                  total = 100;
                  secondValue = Math.max(0, total - firstValue);
                }

                const firstPercentage =
                  total > 0 ? (firstValue / total) * 100 : 0;
                const secondPercentage =
                  total > 0 ? (secondValue / total) * 100 : 0;

                return (
                  <div
                    key={index}
                    style={{
                      marginBottom: "16px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {/* Branch Label and Values */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "60%",
                        }}
                      >
                        {entry[xAxisKey]}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isSingleValue
                          ? `${firstValue}%`
                          : `${firstValue}/${secondValue}`}
                      </span>
                    </div>

                    {/* Progress Bar Container */}
                    <div
                      style={{
                        backgroundColor: "#e5e7eb",
                        borderRadius: "8px",
                        height: "24px",
                        position: "relative",
                        overflow: "hidden",
                        width: "100%",
                        display: "flex",
                      }}
                    >
                      {/* First portion (closed) */}
                      <div
                        style={{
                          width: `${firstPercentage}%`,
                          height: "100%",
                          backgroundColor: colors[0] || "#10b981",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "width 0.8s ease-in-out",
                          borderRadius:
                            firstPercentage === 100 ? "8px" : "8px 0 0 8px",
                        }}
                      >
                        {/* Value Label */}
                        {firstPercentage > 15 && (
                          <span
                            style={{
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "600",
                              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isSingleValue ? `${firstValue}%` : firstValue}
                          </span>
                        )}
                      </div>

                      {/* Second portion (open/remaining) */}
                      {secondPercentage > 0 && (
                        <div
                          style={{
                            width: `${secondPercentage}%`,
                            height: "100%",
                            backgroundColor: isSingleValue
                              ? "#f3f4f6"
                              : colors[1] || "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "width 0.8s ease-in-out",
                            borderRadius:
                              secondPercentage === 100 ? "8px" : "0 8px 8px 0",
                            border: isSingleValue
                              ? "1px solid #d1d5db"
                              : "none",
                          }}
                        >
                          {/* Second Value Label - only show for two-value case */}
                          {secondPercentage > 15 && !isSingleValue && (
                            <span
                              style={{
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "600",
                                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {secondValue}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <AreaChart data={data}>
              {item.gridLines !== false && (
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              )}
              <XAxis dataKey={xAxisKey} stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              {item.legendPosition !== "hidden" && (
                <Legend
                  layout={
                    item.legendPosition === "left" ||
                    item.legendPosition === "right"
                      ? "vertical"
                      : "horizontal"
                  }
                  verticalAlign={
                    item.legendPosition === "bottom"
                      ? "bottom"
                      : item.legendPosition === "top"
                      ? "top"
                      : "middle"
                  }
                  align={
                    item.legendPosition === "left"
                      ? "left"
                      : item.legendPosition === "right"
                      ? "right"
                      : "center"
                  }
                />
              )}
              {yAxisKeys.map((key, index) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stackId="1"
                  stroke={colors[index % colors.length]}
                  fill={colors[index % colors.length]}
                  fillOpacity={(item.areaOpacity || 50) / 100} // Convert percentage to decimal
                  name={yAxisLabels?.[index] || key}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
      case "bar":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <RechartsBarChart
              data={data}
              layout={item.layout || "vertical"} // Add layout prop
            >
              {item.gridLines !== false && (
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              )}
              <XAxis dataKey={xAxisKey} stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value, name, props) => [
                  value,
                  item.yAxisLabels?.[props.dataKey] || name,
                ]}
              />
              {item.legendPosition !== "hidden" && (
                <Legend
                  layout={
                    item.legendPosition === "left" ||
                    item.legendPosition === "right"
                      ? "vertical"
                      : "horizontal"
                  }
                  verticalAlign={
                    item.legendPosition === "bottom"
                      ? "bottom"
                      : item.legendPosition === "top"
                      ? "top"
                      : "middle"
                  }
                  align={
                    item.legendPosition === "left"
                      ? "left"
                      : item.legendPosition === "right"
                      ? "right"
                      : "center"
                  }
                />
              )}
              {yAxisKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={colors[index % colors.length]}
                  radius={[4, 4, 0, 0]}
                  // name={key}
                  name={yAxisLabels?.[index] || key}
                  barSize={item.barWidth || 30} // Add barSize prop
                />
              ))}
            </RechartsBarChart>
          </ResponsiveContainer>
        );
      case "pie":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <RechartsPieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={`${item.innerRadius || 0}%`}
                outerRadius="70%"
                paddingAngle={5}
                dataKey={yAxisKeys[0]}
                nameKey={xAxisKey}
                label={
                  item.showLabels !== false
                    ? ({ name, percent }) => `${(percent * 100).toFixed(0)}%`
                    : false
                }
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.color || chartColors[index % chartColors.length]
                    }
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value, name, props) => [
                  value,
                  item.yAxisLabels?.[props.dataKey] || name,
                ]}
              />
              {item.legendPosition !== "hidden" && (
                <Legend
                  layout={
                    item.legendPosition === "left" ||
                    item.legendPosition === "right"
                      ? "vertical"
                      : "horizontal"
                  }
                  verticalAlign={
                    item.legendPosition === "bottom"
                      ? "bottom"
                      : item.legendPosition === "top"
                      ? "top"
                      : "middle"
                  }
                  align={
                    item.legendPosition === "left"
                      ? "left"
                      : item.legendPosition === "right"
                      ? "right"
                      : "center"
                  }
                />
              )}
            </RechartsPieChart>
          </ResponsiveContainer>
        );
      case "scatter":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <ScatterChart data={data}>
              {item.gridLines !== false && (
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              )}
              <XAxis
                type="number"
                dataKey={xAxisKey}
                name={xAxisKey}
                stroke="#666"
              />
              <YAxis
                type="number"
                dataKey={yAxisKeys[0]}
                name={yAxisKeys[0]}
                stroke="#666"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                cursor={{ strokeDasharray: "3 3" }}
                formatter={(value, name, props) => [
                  value,
                  item.yAxisLabels?.[props.dataKey] || name,
                ]}
              />
              {item.legendPosition !== "hidden" && (
                <Legend
                  layout={
                    item.legendPosition === "left" ||
                    item.legendPosition === "right"
                      ? "vertical"
                      : "horizontal"
                  }
                  verticalAlign={
                    item.legendPosition === "bottom"
                      ? "bottom"
                      : item.legendPosition === "top"
                      ? "top"
                      : "middle"
                  }
                  align={
                    item.legendPosition === "left"
                      ? "left"
                      : item.legendPosition === "right"
                      ? "right"
                      : "center"
                  }
                />
              )}
              <Scatter
                name={item.yAxisLabels?.[0] || yAxisKeys[0] || item.title}
                data={data}
                fill={colors[0]}
                shape="circle"
              />
            </ScatterChart>
          </ResponsiveContainer>
        );
      case "radar":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <RadarChart data={data}>
              <PolarGrid />
              <PolarAngleAxis dataKey={xAxisKey} />
              <PolarRadiusAxis />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Legend />
              {yAxisKeys.map((key, index) => (
                <Radar
                  key={key}
                  // name={key}
                  name={yAxisLabels?.[index] || key}
                  dataKey={key}
                  stroke={colors[index % colors.length]}
                  fill={colors[index % colors.length]}
                  fillOpacity={0.6}
                />
              ))}
            </RadarChart>
          </ResponsiveContainer>
        );
     
      case "composed":
        // Dynamically determine which field should be the bar (highest average value)
        const fieldAverages = yAxisKeys.map((key) => ({
          key,
          average:
            data.reduce((sum, item) => sum + (item[key] || 0), 0) / data.length,
        }));

        // Sort by average value to find the highest
        fieldAverages.sort((a, b) => b.average - a.average);
        const barField = fieldAverages[0]?.key;
        const lineFields = fieldAverages.slice(1).map((f) => f.key);

        return (
          <ResponsiveContainer
            key={`${item.id}-${item.chartType}-${JSON.stringify(item.data)}`}
            width="100%"
            height={isExpanded ? 400 : 300}
          >
            <ComposedChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              {item.gridLines !== false && (
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              )}
              <XAxis
                dataKey={xAxisKey}
                stroke="#666"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis stroke="#666" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              {item.legendPosition !== "hidden" && (
                <Legend
                  layout={
                    item.legendPosition === "left" ||
                    item.legendPosition === "right"
                      ? "vertical"
                      : "horizontal"
                  }
                  verticalAlign={
                    item.legendPosition === "bottom"
                      ? "bottom"
                      : item.legendPosition === "top"
                      ? "top"
                      : "middle"
                  }
                  align={
                    item.legendPosition === "left"
                      ? "left"
                      : item.legendPosition === "right"
                      ? "right"
                      : "center"
                  }
                />
              )}

              {/* Bar for the field with highest average value - use correct color index */}
              {barField && (
                <Bar
                  dataKey={barField}
                  fill={
                    colors[yAxisKeys.indexOf(barField)] ||
                    colors[0] ||
                    "#8B5CF6"
                  }
                  name={yAxisLabels?.[yAxisKeys.indexOf(barField)] || barField}
                  radius={[2, 2, 0, 0]}
                />
              )}

              {/* Lines for remaining fields - use correct color indices */}
              {lineFields.map((field) => {
                const fieldIndex = yAxisKeys.indexOf(field);
                return (
                  <Line
                    key={field}
                    type="monotone"
                    dataKey={field}
                    stroke={
                      colors[fieldIndex] ||
                      chartColors[fieldIndex % chartColors.length]
                    }
                    strokeWidth={3}
                    name={yAxisLabels?.[fieldIndex] || field}
                    dot={{
                      fill:
                        colors[fieldIndex] ||
                        chartColors[fieldIndex % chartColors.length],
                      strokeWidth: 2,
                      r: 4,
                    }}
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        );

      case "stackedBar":
        return (
          <ResponsiveContainer width="100%" height={isExpanded ? 400 : 300}>
            <RechartsBarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xAxisKey} />
              <YAxis />
              <Tooltip />
              <Legend />
              {yAxisKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={colors[index % colors.length]}
                  // name={key}
                  name={yAxisLabels?.[index] || key}
                />
              ))}
            </RechartsBarChart>
          </ResponsiveContainer>
        );
      case "trend":
        return (
          <TrendChart
            data={data}
            xAxisKey={xAxisKey}
            yAxisKeys={yAxisKeys}
            colors={colors}
            yAxisLabels={item.yAxisLabels}
          />
        );
      default:
        return null;
    }
  };
  const renderMap = (item) => {
    const baseLayers = {
      Standard: L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      ),
      Satellite: L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
      ),
      Terrain: L.tileLayer("https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"),
      Hybrid: L.tileLayer(
        "https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
      ),

      Streets: L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
      ),
      Road: L.tileLayer(
        "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
      ),
    };

    const getIconForSource = (source) => {
      const size = iconSizes?.[source] || [32, 32]; // Use the passed iconSizes prop
      if (uploadedIcons[source]) {
        return new L.Icon({
          iconUrl: uploadedIcons[source],
          iconSize: size,
          iconAnchor: [size[0] / 2, size[1]],
          popupAnchor: [0, -size[1]],
        });
      }
      return iconMap[source] || iconMap.default;
    };

    const MapMarkers = () => {
      const map = useMap();

      useEffect(() => {
        if (!map) return;

        // ✅ Expose map globally for external access
        window.leafletMap = map;

        const currentBaseLayerName = Object.keys(baseLayers).find((key) =>
          map.hasLayer(baseLayers[key])
        );
        const activeBaseLayer = currentBaseLayerName
          ? baseLayers[currentBaseLayerName]
          : baseLayers.Standard;

        activeBaseLayer.addTo(map);

        const overlayLayers = {};
        const selectedSources = Array.isArray(item.dataSource)
          ? item.dataSource
          : item.dataSource
          ? [item.dataSource]
          : [];

        selectedSources.forEach((source) => {
          const group = L.layerGroup();
          item.data.points
            .filter((point) => point.type === source)
            .forEach((point) => {
              const fieldsConfig = item.fieldsBySource?.[source] || [];
              const fieldsToDisplay = fieldsConfig
                .filter((field) => field.selected)
                .sort((a, b) => (a.order || 0) - (b.order || 0));

              const popupContent = `
  <div style="
    max-height: 180px;
    overflow-y: auto;
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: white;
    width: 220px;
    font-family: sans-serif;
  ">
    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
      <img src="${
        getIconForSource(source).options.iconUrl
      }" width="20" height="20" />
      <span style="font-weight: 700; font-size: 16px; color: #1a7a73;">${source}</span>
    </div>
    <div style="display: grid; grid-template-columns: auto 1fr; gap: 6px; font-size: 14px;">
      ${fieldsToDisplay
        .map(
          (field) => `
            <div style="font-weight: 600; color: #4b5563;">${
              field.label || field.key
            }:</div>
            <div style="color: #1f2937;">${point[field.key] || "N/A"}</div>
          `
        )
        .join("")}
    </div>
  </div>
`;

              L.marker([point.lat, point.lng], {
                icon: getIconForSource(source),
              })
                .bindPopup(popupContent)
                .addTo(group);
            });

          overlayLayers[source] = group;
          group.addTo(map);
        });

        const control = L.control
          .layers(baseLayers, overlayLayers, {
            position: "topright",
            collapsed: false,
          })
          .addTo(map);

        const geocoderControl = L.Control.geocoder({
          defaultMarkGeocode: true,
          position: "topleft",
        })
          .on("markgeocode", function (e) {
            map.setView(e.geocode.center, 14);
          })
          .addTo(map);

        setTimeout(() => {
          const geocoderEl = document.querySelector(
            ".leaflet-control-geocoder"
          );
          if (geocoderEl) geocoderEl.style.marginTop = "10px";
        }, 0);

        setTimeout(() => {
          const labels = document.querySelectorAll(
            ".leaflet-control-layers-overlays label"
          );
          labels.forEach((label) => {
            const source = label.textContent.trim();
            if (label.querySelector("img")) return;
            const iconUrl =
              uploadedIcons?.[source] ||
              iconMap[source]?.options?.iconUrl ||
              iconMap.default.options.iconUrl;

            if (iconUrl) {
              const img = document.createElement("img");
              img.src = iconUrl;
              img.style.width = "18px";
              img.style.height = "18px";
              img.style.marginRight = "10px";
              img.style.verticalAlign = "middle";
              label.insertBefore(img, label.firstChild.nextSibling);
            }
          });
        }, 500);

        if (item.data.points?.length > 0) {
          const bounds = L.latLngBounds(
            item.data.points.map((p) => [p.lat, p.lng])
          );
          map.fitBounds(bounds, { padding: [50, 50] });
        }

        return () => {
          map.removeControl(control);
          map.removeControl(geocoderControl);
          Object.values(overlayLayers).forEach((layer) =>
            map.removeLayer(layer)
          );
          Object.values(baseLayers).forEach((layer) => {
            if (map.hasLayer(layer) && layer !== activeBaseLayer) {
              map.removeLayer(layer);
            }
          });
        };
      }, [item.data, item.dataSource, item.fieldsBySource]);

      return null;
    };

    return (
      <div style={{ height: "400px", width: "100%", position: "relative" }}>
        <MapContainer
          // key={`${item.id}-${JSON.stringify(item.dataSource)}`}
          key={`${item.id}-${item.zoom}`}
          center={item.center || [25.5941, 85.1376]}
          zoom={item.zoom || 13}
          style={{ height: "100%", width: "100%", borderRadius: "8px" }}
        >
          <MapMarkers />

         
        </MapContainer>

        <style>{`
        .map-legend-horizontal {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 6px 12px;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          z-index: 1000;
          display: flex;
          gap: 15px;
          align-items: center;
          font-size: 12px;
        }
        .map-legend-horizontal span {
          display: flex;
          align-items: center;
          gap: 5px;
        }
      `}</style>
      </div>
    );
  };

  const [, drop] = useDrop({
    accept: [ItemTypes.CARD, ItemTypes.CHART, ItemTypes.TABLE],
    hover: (draggedItem) => {
      if (!isRemoved && draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
    drop: (draggedItem) => {
      if (isRemoved && handleRestore) {
        handleRestore(draggedItem.id);
      }
    },
  });

  drag(drop(ref));

  const opacity = isDragging ? 0.4 : 1;

  if (item.type === "card") {
    return (
      <div
        ref={ref}
        style={{
          position: "relative",
          background: item.color,
          borderRadius: "12px",
          padding: "20px",
          color: item.textColor || "white",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s",
          transform: "translateY(0)",
          cursor: "move",
          opacity,
          border: isRemoved ? "2px dashed rgba(255,255,255,0.5)" : "none",
          textAlign: item.textAlignment || "left",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => {
          if (!isDragging && isRemoved && handleDelete) {
            handleDelete(item.id);
          }
        }}
      >
        {isRemoved && (
          <div
            style={{
              position: "absolute",
              top: "-10px",
              right: "-10px",
              backgroundColor: "#EF4444",
              color: "white",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              cursor: "pointer",
              zIndex: 10,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (handleDelete) handleDelete(item.id);
            }}
          >
            <X size={16} />
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "40px",
                minHeight: "40px",
                borderRadius: "50%", // Makes it fully rounded
                overflow: "hidden",
                marginTop: "10px",
                backgroundColor: "lightgray", // Prevents icon overflow
                // Optional: adds a background
              }}
            >
              {renderIcon(item.icon)}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {editingId === item.id && !isRemoved ? (
                <input
                  value={item.title}
                  onChange={(e) => handleEdit(item.id, "title", e.target.value)}
                  onBlur={() => setEditingId(null)}
                  style={{
                    backgroundColor: "transparent",
                    borderBottom: "1px solid rgba(255,255,255,0.3)",
                    color: item.textColor || "white",
                    fontSize: "18px",
                    fontWeight: "600",
                    outline: "none",
                    width: "100%",
                  }}
                  autoFocus
                />
              ) : (
                <h3
                  style={{
                    margin: 0,
                    fontWeight: "600",
                    cursor: "pointer",
                    marginTop: "10px",
                    transition: "opacity 0.2s",
                    fontSize:
                      item.fontSize === "custom"
                        ? `${item.customFontSize}px`
                        : item.fontSize === "small"
                        ? "16px"
                        : item.fontSize === "medium"
                        ? "18px"
                        : item.fontSize === "large"
                        ? "20px"
                        : item.fontSize === "extra-large"
                        ? "24px"
                        : "18px",
                    color: item.textColor || "white",
                    lineHeight: 1.2,
                  }}
                  onClick={() => !isRemoved && setEditingId(item.id)}
                  onMouseOver={(e) => (e.target.style.opacity = "0.8")}
                  onMouseOut={(e) => (e.target.style.opacity = "1")}
                >
                  {item.title}
                </h3>
              )}
              <div
                style={{
                  color: item.textColor || "white",
                  fontWeight: "bold",
                  fontSize: "18px",
                  lineHeight: 1.3,
                }}
              >
                {item.subtitle}
              </div>
            </div>
          </div>

          {!isRemoved && (
            <div style={{ display: "flex", gap: "8px" }}>
              {isEditingLayout && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(item.id);
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    border: "none",
                    borderRadius: "4px",
                    color: "white",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "rgba(255,255,255,0.3)")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "rgba(255,255,255,0.2)")
                  }
                >
                  <PlusCircle size={16} />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(item.id, "openEditPopup");
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "4px",
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#e5e7eb")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "#f3f4f6")}
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(item.id);
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "4px",
                  color: "white",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "rgba(255,255,255,0.3)")
                }
                onMouseOut={(e) =>
                  (e.target.style.backgroundColor = "rgba(255,255,255,0.2)")
                }
              >
                <Trash size={16} />
              </button>
            </div>
          )}
        </div>

        <div>
          {/* <div style={{color: item.textColor || "white", fontWeight: "bold", marginBottom: "5px",fontSize:'14px',marginLeft:'5px' }}>
          {item.value}
        </div> */}

          {item.dataSource && (
            <div>
              

              {isDetailsExpanded && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: item.textColor || "white",
                  }}
                >
                  <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                    {item.dataSource}
                  </div>

                  {item.dataSource === "Bank-wise Deposits" && (
                    <>
                      {item.bankName && <div>Bank Name: {item.bankName}</div>}
                      {item.depositType && (
                        <div>Deposit Type: {item.depositType}</div>
                      )}
                      {item.amountFrom && (
                        <div>Amount From: {item.amountFrom}</div>
                      )}
                      {item.amountTo && <div>Amount To: {item.amountTo}</div>}
                      {item.region && <div>Region: {item.region}</div>}
                    </>
                  )}

                  {item.dataSource === "Monthly Transactions" && (
                    <>
                      {item.transactionType && (
                        <div>Transaction Type: {item.transactionType}</div>
                      )}
                      {item.monthYear && (
                        <div>Month/Year: {item.monthYear}</div>
                      )}
                      {item.accountType && (
                        <div>Account Type: {item.accountType}</div>
                      )}
                      {item.transactionCategory && (
                        <div>
                          Transaction Category: {item.transactionCategory}
                        </div>
                      )}
                     
                    </>
                  )}

                  {item.dataSource === "Yearly Transactions" && (
                    <>
                      {item.year && <div>Year: {item.year}</div>}
                      {item.quarter && <div>Quarter: {item.quarter}</div>}
                      {item.transactionType && (
                        <div>Transaction Type: {item.transactionType}</div>
                      )}
                      {item.accountType && (
                        <div>Account Type: {item.accountType}</div>
                      )}
                      
                    </>
                  )}

                  {item.dataSource === "Loan Transactions" && (
                    <>
                      {item.loanType && <div>Loan Type: {item.loanType}</div>}
                      {item.transactionType && (
                        <div>Transaction Type: {item.transactionType}</div>
                      )}
                      {item.loanStatus && (
                        <div>Loan Status: {item.loanStatus}</div>
                      )}
                      
                    </>
                  )}

                  {item.dataSource === "Total ATMs per district" && (
                    <>
                      {item.atmType && <div>ATM Type: {item.atmType}</div>}
                      {item.operationalStatus && (
                        <div>Operational Status: {item.operationalStatus}</div>
                      )}
                      
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  } else if (item.type === "table") {
    return (
      <div
        ref={ref}
        style={{
          position: "relative",
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s",
          opacity,
          gridColumn:
            expandLevel === 3
              ? "span 3"
              : expandLevel === 2
              ? "span 2"
              : "span 1",
          gridRow:
            heightExpandLevel === 3
              ? "span 3"
              : heightExpandLevel === 2
              ? "span 2"
              : "span 1",
          height:
            heightExpandLevel === 3
              ? "900px"
              : heightExpandLevel === 2
              ? "600px"
              : "370px",
          border: isRemoved ? "2px dashed #e5e7eb" : "none",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => {
          if (!isDragging && isRemoved && handleDelete) {
            handleDelete(item.id);
          }
        }}
      >
        {isRemoved && (
          <div
            style={{
              position: "absolute",
              top: "-10px",
              right: "-10px",
              backgroundColor: "#EF4444",
              color: "white",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              cursor: "pointer",
              zIndex: 10,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (handleDelete) handleDelete(item.id);
            }}
          >
            <Trash size={16} />
          </div>
        )}

        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            opacity: 0.5,
            transition: "opacity 0.2s",
          }}
        >
          <Move size={16} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Table size={24} color="#111827" />
            {editingId === item.id && !isRemoved ? (
              <input
                value={item.title}
                onChange={(e) => handleEdit(item.id, "title", e.target.value)}
                onBlur={() => setEditingId(null)}
                style={{
                  borderBottom: "1px solid #d1d5db",
                  outline: "none",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                }}
                autoFocus
              />
            ) : (
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                  cursor: "pointer",
                  transition: "color 0.2s",
                }}
                onClick={() => !isRemoved && setEditingId(item.id)}
                onMouseOver={(e) => (e.target.style.color = "#1a7a73")}
                onMouseOut={(e) => (e.target.style.color = "#111827")}
              >
                {item.title}
              </h3>
            )}
          </div>
          {!isRemoved && (
            <div style={{ display: "flex", gap: "8px" }}>
              {isEditingLayout && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(item.id);
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  <PlusCircle size={16} />
                </button>
              )}
              {!isViewMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item.id, "openEditPopup");
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  <Edit2 size={16} />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHeightExpand();
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "4px",
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                title={`Expand height (current: ${heightExpandLevel})`}
              >
                {heightExpandLevel === 3 ? (
                  <ChevronUp size={16} />
                ) : heightExpandLevel === 2 ? (
                  <ChevronsDown size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand();
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "4px",
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                title={`Expand width (current: ${expandLevel})`}
              >
                {expandLevel === 3 ? (
                  <ChevronLeft size={16} />
                ) : expandLevel === 2 ? (
                  <ChevronsRight size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
              {!isViewMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  <Trash size={16} />
                </button>
              )}
            </div>
          )}
        </div>
        {(!item.minimized || isRemoved) && renderTable(item)}
      </div>
    );
  } else if (item.type === "map") {
    return (
      <div
        ref={ref}
        style={{
          position: "relative",
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s",
          opacity,
          gridColumn:
            expandLevel === 3
              ? "span 3"
              : expandLevel === 2
              ? "span 2"
              : "span 1",
          gridRow:
            heightExpandLevel === 3
              ? "span 3"
              : heightExpandLevel === 2
              ? "span 2"
              : "span 1",
          height:
            heightExpandLevel === 3
              ? "900px"
              : heightExpandLevel === 2
              ? "600px"
              : "370px",
          border: isRemoved ? "2px dashed #e5e7eb" : "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Similar header to other widgets */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <MapPin size={24} color="#111827" />
            {editingId === item.id && !isRemoved ? (
              <input
                value={item.title}
                onChange={(e) => handleEdit(item.id, "title", e.target.value)}
                onBlur={() => setEditingId(null)}
                style={{
                  borderBottom: "1px solid #d1d5db",
                  outline: "none",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                }}
                autoFocus
              />
            ) : (
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                  cursor: "pointer",
                  transition: "color 0.2s",
                }}
                onClick={() => !isRemoved && setEditingId(item.id)}
                onMouseOver={(e) => (e.target.style.color = "#1a7a73")}
                onMouseOut={(e) => (e.target.style.color = "#111827")}
              >
                {item.title}
              </h3>
            )}
          </div>

          {!isRemoved && (
            <div style={{ display: "flex", gap: "8px" }}>
              {isEditingLayout && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(item.id);
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  <PlusCircle size={16} />
                </button>
              )}
              {!isViewMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item.id, "openEditPopup");
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  <Edit2 size={16} />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHeightExpand();
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "4px",
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                title={`Expand height (current: ${heightExpandLevel})`}
              >
                {heightExpandLevel === 3 ? (
                  <ChevronUp size={16} />
                ) : heightExpandLevel === 2 ? (
                  <ChevronsDown size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand();
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "4px",
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                title={`Expand width (current: ${expandLevel})`}
              >
                {expandLevel === 3 ? (
                  <ChevronLeft size={16} />
                ) : expandLevel === 2 ? (
                  <ChevronsRight size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
              {!isViewMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  <Trash size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        {(!item.minimized || isRemoved) && renderMap(item)}
      </div>
    );
  } else {
    return (
      <div
        ref={ref}
        style={{
          position: "relative",
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s",
          opacity,
          gridColumn:
            expandLevel === 3
              ? "span 3"
              : expandLevel === 2
              ? "span 2"
              : "span 1",
          gridRow:
            heightExpandLevel === 3
              ? "span 3"
              : heightExpandLevel === 2
              ? "span 2"
              : "span 1",
          height:
            heightExpandLevel === 3
              ? "900px"
              : heightExpandLevel === 2
              ? "600px"
              : "370px",
          border: isRemoved ? "2px dashed #e5e7eb" : "none",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => {
          if (!isDragging && isRemoved && handleDelete) {
            handleDelete(item.id);
          }
        }}
      >
        {isRemoved && !isViewMode && (
          <div
            style={{
              position: "absolute",
              top: "-10px",
              right: "-10px",
              backgroundColor: "#EF4444",
              color: "white",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              cursor: "pointer",
              zIndex: 10,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (handleDelete) handleDelete(item.id);
            }}
          >
            <Trash size={16} />
          </div>
        )}

        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            opacity: 0.5,
            transition: "opacity 0.2s",
          }}
        >
          <Move size={16} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {item.chartType === "line" && (
              <LineChartIcon size={24} color="#111827" />
            )}
            {item.chartType === "area" && (
              <AreaChartIcon size={24} color="#111827" />
            )}
            {item.chartType === "bar" && (
              <BarChart3 size={24} color="#111827" />
            )}
            {item.chartType === "pie" && (
              <PieChartIcon size={24} color="#111827" />
            )}
            {item.chartType === "scatter" && (
              <Scatter size={24} color="#111827" />
            )}
            {item.chartType === "radar" && <Radar size={24} color="#111827" />}
            {/* {item.chartType === "funnel" && (
              <Funnel size={24} color="#111827" />
            )} */}
            {editingId === item.id && !isRemoved ? (
              <input
                value={item.title}
                onChange={(e) => handleEdit(item.id, "title", e.target.value)}
                onBlur={() => setEditingId(null)}
                style={{
                  borderBottom: "1px solid #d1d5db",
                  outline: "none",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                }}
                autoFocus
              />
            ) : (
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                  cursor: "pointer",
                  transition: "color 0.2s",
                }}
                onClick={() => !isRemoved && setEditingId(item.id)}
                onMouseOver={(e) => (e.target.style.color = "#1a7a73")}
                onMouseOut={(e) => (e.target.style.color = "#111827")}
              >
                {item.title}
              </h3>
            )}
          </div>
          {!isRemoved && (
            <div style={{ display: "flex", gap: "8px" }}>
              {isEditingLayout && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(item.id);
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  <PlusCircle size={16} />
                </button>
              )}
              {!isViewMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item.id, "openEditPopup");
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  <Edit2 size={16} />
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHeightExpand();
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "4px",
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                title={`Expand height (current: ${heightExpandLevel})`}
              >
                {heightExpandLevel === 3 ? (
                  <ChevronUp size={16} />
                ) : heightExpandLevel === 2 ? (
                  <ChevronsDown size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand();
                }}
                style={{
                  padding: "4px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "4px",
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                title={`Expand width (current: ${expandLevel})`}
              >
                {expandLevel === 3 ? (
                  <ChevronLeft size={16} />
                ) : expandLevel === 2 ? (
                  <ChevronsRight size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
              {!isViewMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                  style={{
                    padding: "4px",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    borderRadius: "4px",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  <Trash size={16} />
                </button>
              )}
            </div>
          )}
        </div>
        {(!item.minimized || isRemoved) && renderChart(item)}
      </div>
    );
  }
};